import type { Assessment, ClassData, CurriculumLink, MarkSetCategory, StreamPost } from '../types';
import type { ImportGap } from './importGaps';

/**
 * classDuplication — build a fresh class/template from an existing one.
 *
 * Pure and dependency-free so BOTH sides can run it: the Duplicate/Save-as-Template
 * modal in the browser, and the server-side template share (which writes a copy into
 * ANOTHER teacher's account and therefore can't go through the client at all).
 *
 * The rules here are load-bearing and were learned the hard way — a copy must never
 * carry the source's roster, grades, Google Classroom links, or (most importantly)
 * its uploaded-file references. Keeping one implementation means a fix to any of
 * that can't apply to one path and silently miss the other.
 */

export interface DuplicateOptions {
  name: string;
  code: string;
  semester: string;
  academicYear: string;
  markSetId: string;
  themeColor: string;
  includeAssessments: boolean;
  includePosts: boolean;
  includeSeatingLayout: boolean;
  /** Result is a reusable template (no roster, everything as drafts). */
  asTemplate?: boolean;
  /** The curriculum chosen in the form — may differ from the source's. */
  curriculumLink?: CurriculumLink;
  /**
   * The SOURCE class's mark-set categories, and the chosen one's.
   *
   * Needed because every assessment stores a `categoryId`, and those ids belong
   * to the mark set it was created under. Copy them into a class using a
   * different mark set and every id is a stranger — see remapCategoryId.
   *
   * Optional: when the mark set is unchanged there is nothing to remap, and the
   * server-side share path may not have both to hand.
   */
  sourceCategories?: MarkSetCategory[];
  targetCategories?: MarkSetCategory[];
  /** Injected so the function stays pure/deterministic for tests. */
  newId?: (seed: string) => string;
}

/**
 * Translate an assessment's category from one mark set to another.
 *
 * Assessments reference a category by ID, and those IDs are generated per mark
 * set — so "Knowledge" in the Ontario KATC set and "Knowledge" in a teacher's
 * own copy of it are different strings. Duplicating a class and choosing a
 * different mark set therefore left every assessment pointing at a category
 * that did not exist in its new class: they vanished from the category views
 * and collapsed into the first column, which reads as "the assessments did not
 * come across".
 *
 * Matched by NAME first, because that is what the teacher means when they pick
 * a mark set with the same categories — KATC to KATC is the common case, and
 * the names are stable while the ids are not. Position is the fallback for
 * renamed-but-parallel sets (a 4-category set mapping onto another 4-category
 * set), and only when both fail does it land in the first category — which is
 * at least visible, and visibly wrong, rather than silently absent.
 */
export function remapCategoryId(
  categoryId: string,
  source: MarkSetCategory[] | undefined,
  target: MarkSetCategory[] | undefined,
): string {
  if (!target || target.length === 0) return categoryId;
  // Same id already valid in the target: same mark set, or sets that share ids.
  if (target.some(c => c.id === categoryId)) return categoryId;

  const from = source?.find(c => c.id === categoryId);
  if (from) {
    const key = (n: string) => n.trim().toLowerCase();
    const byName = target.find(c => key(c.name) === key(from.name));
    if (byName) return byName.id;
    const index = source!.indexOf(from);
    if (index >= 0 && index < target.length) return target[index].id;
  }
  return target[0].id;
}

const defaultNewId = (seed: string) =>
  `${seed}_copy_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

export const DEFAULT_LEARNING_SKILLS = [
  'Responsibility', 'Organization', 'Independent Work',
  'Collaboration', 'Initiative', 'Self Regulation',
];

/**
 * Carry a rebuild checklist into a copy, but strip what belonged to the ORIGINAL
 * import. `sourceUrl` points into the source teacher's own Google Classroom course
 * — on a shared template that's a dead link behind Google's auth wall for the
 * recipient, and it discloses the sharer's course URL. `resolved` is the sharer's
 * progress, not the recipient's, so a copy starts unticked.
 */
const copyImportGaps = (gaps: ImportGap[] | undefined): ImportGap[] | undefined =>
  (gaps && gaps.length
    ? gaps.map(({ sourceUrl: _s, resolved: _r, ...rest }) => rest)
    : undefined);


/**
 * Drop keys whose value is `undefined`, at every depth.
 *
 * Firestore REJECTS undefined, and this builder legitimately produces it two
 * ways: optional class fields (useNicknames, disableStudentHub, reportMode…)
 * are copied straight from a source that may never have set them, and the
 * assessment mapper assigns `undefined` deliberately to unlink a copy from its
 * origin (streamPostId, gcSourceId, date…).
 *
 * Every consumer that goes through createClass survived, because that strips
 * before addDoc. Publishing to the shared template library does not — it writes
 * the snapshot straight through a transaction — so an ordinary class threw
 * "Unsupported field value: undefined (found in field snapshot.useNicknames)".
 * Stripping in the BUILDER fixes it for every consumer, including ones written
 * later, rather than patching the one call site that happened to surface it.
 *
 * Removing the key is the correct semantic: absent means "never set", which is
 * exactly what an unset optional field means. `null` and `false` are real
 * values and are preserved — dropping `false` would silently flip a setting on.
 */
/**
 * Drop `undefined` recursively.
 *
 * Exported because a cross-post writes these objects into an `arrayUnion`, and
 * Firestore rejects `undefined` inside one — while removeUndefined deliberately
 * does NOT recurse into FieldValue sentinels, so cleaning after wrapping is a
 * no-op. The elements have to be clean before they go in.
 */
export function stripUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    // Drop undefined ELEMENTS too, not just undefined properties. Firestore
    // rejects `[1, undefined]` exactly as hard as `{a: undefined}`, and the
    // comment above promises "every depth" — a future mapper returning
    // undefined for a filtered item would otherwise reintroduce the original
    // crash through the very path this function exists to protect.
    return value.filter(v => v !== undefined).map(v => stripUndefinedDeep(v)) as unknown as T;
  }
  if (value === null || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = stripUndefinedDeep(v);
  }
  return out as T;
}

/**
 * Strip a stream post of everything that belongs to the class it came from.
 *
 * ONE definition, used by class duplication AND by cross-posting. The
 * cross-post planner originally re-derived this list from scratch and missed
 * four things this function had handled for months — most seriously the Storage
 * references below. A second copy of a strip list is a second chance to forget
 * an item.
 */
export function stripPostForCopy(p: StreamPost, id: string, isDraft: boolean): StreamPost {
  return {
    ...p,
    id,
    isDraft,
    isPinned: false,
    isHighlighted: false,
    originSourceId: p.originSourceId || p.gcSourceId, // stable id so re-import dedups
    gcSourceId: undefined,
    replies: [],
    assessmentId: undefined,
    /**
     * CRITICAL: do NOT carry uploaded-file references — they point at the SOURCE
     * class's Storage objects, so deleting the copied post would delete the
     * original's files. Links/resources (URLs) are fine and stay via ...p.
     */
    imageUrls: undefined,
    imagePaths: undefined,
    attachments: undefined,
    importGaps: copyImportGaps(p.importGaps),
  };
}

/**
 * Strip an assessment of state keyed to the source class.
 *
 * `categoryId` is passed in already remapped — the caller knows the target's
 * categories; this function only owns what must be DROPPED.
 */
export function stripAssessmentForCopy(
  a: Assessment,
  id: string,
  categoryId: string,
  opts: { isDraft: boolean; dropCurriculum: boolean; dropCategoryWeights?: boolean },
): Assessment {
  return {
    ...a,
    id,
    categoryId,
    isDraft: opts.isDraft,
    // Expectation codes are tagged to the source's curriculum doc.
    ...(opts.dropCurriculum ? { curriculumExpectations: undefined } : {}),
    streamPostId: undefined,
    originSourceId: a.originSourceId || a.gcSourceId,
    gcSourceId: undefined,
    date: undefined,
    // Source-class grade/push state — keyed by the OTHER class's students, and
    // it would tell the target's UI that grades were pushed and students
    // notified when neither happened for them.
    pushedScores: undefined,
    gradesPushedAt: undefined,
    gradeNotifiedAt: undefined,
    gradeNotifiedStudentIds: undefined,
    /**
     * Per-category weights are keyed by the SOURCE mark set's category ids.
     *
     * Dropped only when asked. LEGACY BUNDLE ROWS CARRY THEIR WEIGHT HERE AND
     * NOWHERE ELSE — the `weight ?? categoryWeights?.[categoryId] ?? 1` fallback
     * in AssessmentsView exists for exactly them — so dropping this
     * unconditionally silently reset every component of a duplicated bundle to
     * weight 1. That is grading drift in a copy the teacher believes is
     * identical. Duplication into the SAME mark set keeps the keys, which are
     * still valid there; cross-posting drops them, because the target's ids are
     * strangers and bundles are refused anyway.
     */
    ...(opts.dropCategoryWeights ? { categoryWeights: undefined } : {}),
    // Points at a rubric document owned by the source class. An embedded
    // `rubric` object is self-contained and stays.
    rubricId: undefined,
    importGaps: copyImportGaps(a.importGaps),
  };
}

export function buildClassCopy(
  sourceClass: ClassData,
  opts: DuplicateOptions,
): Partial<ClassData> {
  const newId = opts.newId || defaultNewId;

  // Did the teacher change/remove the primary curriculum? If so the class is being
  // reconfigured: the copied assessments' expectation codes (tagged to the OLD doc)
  // no longer apply, and a hidden second curriculum shouldn't ride along. If
  // untouched, this is a faithful copy — carry everything as-is.
  const primaryChanged =
    (opts.curriculumLink?.filename ?? '') !== (sourceClass.curriculumLink?.filename ?? '') ||
    (opts.curriculumLink?.courseCode ?? '') !== (sourceClass.curriculumLink?.courseCode ?? '');

  const out: Partial<ClassData> = {
    name: opts.name,
    code: opts.code,
    semester: opts.semester,
    academicYear: opts.academicYear,
    markSetId: opts.markSetId,
    ...(sourceClass.level ? { level: sourceClass.level } : {}),
    themeColor: opts.themeColor,
    students: [],      // Always empty roster
    grades: {},        // No grades
    attendance: [],    // No attendance
    stream: [],        // set below (empty, or announcement/resource posts as drafts)
    learningSkillCategories: sourceClass.learningSkillCategories || [...DEFAULT_LEARNING_SKILLS],
    useNicknames: sourceClass.useNicknames,
    // Carried, or duplicating a Privacy First class produced an UNSEALED copy that looked the same
    // to the teacher — nicknames on, the badge gone, and every join path writing real names.
    // Always together with useNicknames: firestore.rules refuses a sealed class without them.
    ...(sourceClass.privacyFirst ? { privacyFirst: true, useNicknames: true } : {}),
    disableStudentHub: sourceClass.disableStudentHub,
    usedNicknames: [],
    /**
     * RESET beside the roster it describes.
     *
     * `memberStudentIds` maps a uid to a studentId on THIS class. The copy has an empty roster, so
     * every entry points at a row that does not exist. Harmless today — `selfStudentIdOf` checks the
     * row is present before trusting the map — but a copy carrying the source class's sign-in links
     * is a thing to reason about every time someone reads this, and resetting it costs one line.
     */
    memberStudentIds: {},
    gamificationSettings: sourceClass.gamificationSettings,
    xpEvents: [],
    reportMode: sourceClass.reportMode,
    acceptingSubmissions: sourceClass.acceptingSubmissions,
    ...(opts.asTemplate ? { isTemplate: true } : {}),
    ...(opts.curriculumLink ? { curriculumLink: opts.curriculumLink } : {}),
    ...(!primaryChanged && sourceClass.curriculumLink2 ? { curriculumLink2: sourceClass.curriculumLink2 } : {}),
  };

  if (opts.includeAssessments && sourceClass.assessments.length > 0) {
    out.assessments = sourceClass.assessments.map(a => stripAssessmentForCopy(
      a,
      newId(a.id),
      // The category has to be translated, not copied: its id belongs to the
      // source's mark set. This is the whole bug — see remapCategoryId.
      remapCategoryId(a.categoryId, opts.sourceCategories, opts.targetCategories),
      {
        isDraft: true,
        dropCurriculum: primaryChanged,
        // Only when the categories actually changed identity — otherwise a
        // legacy bundle silently loses the weights it stores nowhere else.
        dropCategoryWeights: opts.targetCategories !== opts.sourceCategories,
      },
    ));
  } else {
    out.assessments = [];
  }

  // Announcements & resource posts as UNLINKED drafts (the "template → fresh
  // Classroom" flow). Student questions/replies and assignment posts are not
  // carried — assignments live as draft assessments above.
  if (opts.includePosts) {
    out.stream = (sourceClass.stream || [])
      .filter(p => p.type === 'announcement' || p.type === 'resource')
      .map(p => stripPostForCopy(p, newId(p.id), true));
  }

  if (opts.includeSeatingLayout && sourceClass.seatingChart) {
    out.seatingChart = {
      ...sourceClass.seatingChart,
      positions: {}, // Clear student positions
      seats: {},     // Clear legacy seats too
    };
  }

  return stripUndefinedDeep(out);
}
